import { useEffect, useMemo, useState } from "react";
import { StatusPanel } from "./StatusPanel";
import { payoutModelLabel } from "../lib/compensation";
import {
  finalizeTask,
  saveTargetEvent,
  saveTaskEvidence,
  saveTaskProgress,
  submitMetricSnapshot,
  uploadTaskEvidence,
} from "../lib/workflowService";
import type { AssignmentTarget, EvidenceRequirement, TargetEventType, TaskAssignment, TaskOffer, TaskStep } from "../lib/workflowTypes";

export function TaskExecutionPanel({
  task,
  onChanged,
  onSubmitted,
  onReleased,
  busy,
  qaOnly = false,
  submitLabel,
  releaseLabel,
  onSubmitTask,
}: {
  task: TaskAssignment;
  onChanged: () => void;
  onSubmitted: () => void | Promise<void>;
  onReleased: () => void;
  busy: boolean;
  qaOnly?: boolean;
  submitLabel?: string;
  releaseLabel?: string;
  onSubmitTask?: (task: TaskAssignment, evidence: Record<string, string>) => Promise<void>;
}) {
  const [progress, setProgress] = useState(task.progress);
  const [evidence, setEvidence] = useState(task.evidence);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProgress(task.progress);
    setEvidence(task.evidence);
  }, [task.assignmentId, task.evidence, task.progress]);

  const completed = useMemo(() => task.steps.filter((step) => !step.required || Boolean(progress[step.key])).length, [progress, task.steps]);
  const evidenceComplete = task.evidenceRequirements.every((item) => !item.required || Boolean(evidence[item.key]));
  const targetsComplete = !task.proofPerTarget || !task.targets?.length || task.targets.every((target) => target.status !== "pending");
  const canSubmit = completed === task.steps.length && evidenceComplete && targetsComplete && task.state !== "submitted";

  async function updateStep(step: TaskStep, value: boolean | string | number) {
    const next = { ...progress, [step.key]: value };
    setProgress(next);
    setSaving(true);
    try {
      await saveTaskProgress(task.assignmentId, step.key, value);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save progress.");
    } finally {
      setSaving(false);
    }
  }

  async function updateEvidence(item: EvidenceRequirement, value: string | File) {
    const displayValue = value instanceof File ? value.name : value;
    setEvidence({ ...evidence, [item.key]: displayValue });
    setSaving(true);
    try {
      if (value instanceof File && (item.kind === "file" || item.kind === "screenshot")) {
        await uploadTaskEvidence(task.assignmentId, item.key, value, item.kind);
      } else {
        await saveTaskEvidence(task.assignmentId, item.key, String(value), item.kind);
      }
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save evidence.");
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    setError("");
    try {
      if (onSubmitTask) await onSubmitTask(task, evidence);
      else await finalizeTask(task.assignmentId);
      await onSubmitted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not submit the task.");
    }
  }

  if (task.state === "submitted") {
    return (
      <div className="submitted-state">
        <span>✓</span>
        <h3>{qaOnly ? "QA proof submitted." : "Evidence submitted."}</h3>
        <p>{qaOnly ? "Open Review Flow to approve or reject this internal test." : `A reviewer will check your work against SOP v${task.sopVersion}. Your reward stays pending until approval.`}</p>
      </div>
    );
  }

  return (
    <div className={`active-task ${qaOnly ? "qa-active-task" : ""}`}>
      {qaOnly && <StatusPanel title="Internal QA only" message="This uses the worker task UI, but it cannot create withdrawable earnings." kind="success" />}
      <div className="active-task-title">
        <div>
          <span>{task.route.toUpperCase()} · ${task.rewardUsd}</span>
          <h3>{task.title}</h3>
          <p>{task.summary}</p>
          <small>{task.product.productName} · {payoutModelLabel(task.compensation, task.rewardUsd)}</small>
          {task.controlledRun && (
            <small>
              Controlled run · {task.controlledRun.reviewMode?.replace("_", " ") ?? "review gated"}
              {task.controlledRun.autoDecision ? ` · ${task.controlledRun.autoDecision.toUpperCase()} ${task.controlledRun.autoScore ?? 0}` : ""}
            </small>
          )}
        </div>
        <div className="active-task-meter"><b>{completed}/{task.steps.length}</b><span>STEPS</span></div>
      </div>
      {task.reviewerNote && <StatusPanel title="Changes requested" message={task.reviewerNote} kind="error" />}
      {error && <StatusPanel title="Check the task" message={error} kind="error" />}
      <div className="task-resources">
        <h4>SOP & resources</h4>
        {task.resources.map((resource) => <a href={resource.url} target="_blank" rel="noreferrer" key={resource.id}><span>{resource.kind === "pdf" ? "PDF" : "LINK"}</span>{resource.label}<b>↗</b></a>)}
      </div>
      <div className="task-steps">
        {task.steps.map((step, index) => <TaskStepControl key={step.key} step={step} index={index} value={progress[step.key]} onChange={(value) => updateStep(step, value)} />)}
      </div>
      {Boolean(task.targets?.length) && (
        <TargetBatch
          targets={task.targets ?? []}
          route={task.route}
          metricCapUsd={task.metricCapUsd}
          payoutUnit={task.payoutUnit}
          onChanged={onChanged}
          onSaving={setSaving}
          onError={setError}
        />
      )}
      <div className="task-evidence">
        <div><span>FINAL STEP</span><h4>Add your proof</h4><p>Only submit work you completed. Hide unrelated private information in screenshots.</p></div>
        {task.evidenceRequirements.map((item) => (
          <label key={item.key}>
            <span>{item.label}{item.required && " *"}</span>
            <small>{item.help}</small>
            {item.kind === "file" || item.kind === "screenshot" ? (
              <input type="file" accept={item.allowedMime?.join(",")} onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void updateEvidence(item, file);
              }} />
            ) : <input type={item.kind === "url" ? "url" : item.kind === "metric" ? "number" : "text"} value={evidence[item.key] ?? ""} onChange={(event) => updateEvidence(item, event.target.value)} />}
          </label>
        ))}
      </div>
      {!targetsComplete && <StatusPanel title="Target proof needed" message="Log at least one result/proof action on every target card before final review." />}
      <div className="member-actions">
        <button className="primary" disabled={!canSubmit || saving} onClick={submit}>{saving ? "SAVING..." : submitLabel ?? "SUBMIT FOR REVIEW"}</button>
        <button disabled={busy} onClick={onReleased}>{releaseLabel ?? "RELEASE TASK"}</button>
      </div>
    </div>
  );
}

function TargetBatch({
  targets,
  route,
  metricCapUsd,
  payoutUnit,
  onChanged,
  onSaving,
  onError,
}: {
  targets: AssignmentTarget[];
  route: TaskOffer["route"];
  metricCapUsd?: number;
  payoutUnit?: string;
  onChanged: () => void;
  onSaving: (saving: boolean) => void;
  onError: (message: string) => void;
}) {
  const [forms, setForms] = useState<Record<string, { platform: string; metricUrl: string; views: string; proof: string }>>({});
  const actions = targetActions(route);

  async function log(target: AssignmentTarget, eventType: TargetEventType) {
    onSaving(true);
    onError("");
    try {
      await saveTargetEvent(target.id, eventType, { targetName: target.targetName, route });
      onChanged();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not save target event.");
    } finally {
      onSaving(false);
    }
  }

  async function saveMetric(target: AssignmentTarget) {
    const form = forms[target.id] ?? { platform: "", metricUrl: "", views: "0", proof: "" };
    onSaving(true);
    onError("");
    try {
      await submitMetricSnapshot(target.id, form.platform, form.metricUrl, Number(form.views), { proof: form.proof });
      onChanged();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not save metric proof.");
    } finally {
      onSaving(false);
    }
  }

  return (
    <div className="target-batch">
      <div className="target-batch-head">
        <div><span>TARGET BATCH</span><h4>{targets.length} private work targets</h4><p>{payoutUnit ?? "Paid only for approved target results."}{metricCapUsd ? ` Cap $${metricCapUsd}.` : ""}</p></div>
        <b>{targets.filter((target) => target.status !== "pending").length}/{targets.length}</b>
      </div>
      <div className="target-card-grid">
        {targets.map((target) => {
          const form = forms[target.id] ?? { platform: "", metricUrl: "", views: "", proof: "" };
          return (
            <article className={`target-card ${target.status}`} key={target.id}>
              <div className="target-card-top">
                <span>{target.sourceObject.replace("_", " ").toUpperCase()}</span>
                <b>{target.status.toUpperCase()}</b>
              </div>
              <h5>{target.targetName}</h5>
              <p>{target.icpReason || target.context || "Qualified by the campaign SOP."}</p>
              {target.maskedContact && <small>{target.maskedContact}</small>}
              {target.contactHint && <small>{target.contactHint}</small>}
              {target.targetUrl && <a href={target.targetUrl} target="_blank" rel="noreferrer">OPEN TARGET</a>}
              <div className="target-action-row">
                {actions.map((action) => <button key={action.event} onClick={() => log(target, action.event)}>{action.label}</button>)}
              </div>
              {route === "clipper" && (
                <div className="target-metric-form">
                  <input aria-label={`${target.targetName} platform`} placeholder="Platform" value={form.platform} onChange={(event) => setForms({ ...forms, [target.id]: { ...form, platform: event.target.value } })} />
                  <input aria-label={`${target.targetName} URL`} placeholder="Post URL" value={form.metricUrl} onChange={(event) => setForms({ ...forms, [target.id]: { ...form, metricUrl: event.target.value } })} />
                  <input aria-label={`${target.targetName} views`} type="number" min={0} placeholder="Views" value={form.views} onChange={(event) => setForms({ ...forms, [target.id]: { ...form, views: event.target.value } })} />
                  <input aria-label={`${target.targetName} metric proof`} placeholder="Screenshot/proof note" value={form.proof} onChange={(event) => setForms({ ...forms, [target.id]: { ...form, proof: event.target.value } })} />
                  <button onClick={() => saveMetric(target)}>SAVE METRICS</button>
                </div>
              )}
              {target.latestEvent && <small>Last: {target.latestEvent.replace("_", " ")}</small>}
              {target.latestMetricViews != null && <small>{target.latestMetricViews.toLocaleString()} verified views submitted</small>}
              {target.reviewerNotes && <small>{target.reviewerNotes}</small>}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function targetActions(route: TaskOffer["route"]): Array<{ event: TargetEventType; label: string }> {
  if (route === "closer") return [
    { event: "contacted", label: "CONTACTED" },
    { event: "replied", label: "REPLIED" },
    { event: "booked", label: "BOOKED" },
  ];
  if (route === "seeder") return [
    { event: "published", label: "POSTED" },
    { event: "replied", label: "REPLY" },
  ];
  if (route === "clipper") return [
    { event: "published", label: "PUBLISHED" },
  ];
  return [
    { event: "researched", label: "QUALIFIED" },
    { event: "contacted", label: "CONTACTED" },
    { event: "replied", label: "REPLIED" },
  ];
}

function TaskStepControl({ step, index, value, onChange }: { step: TaskStep; index: number; value: boolean | string | number | undefined; onChange: (value: boolean | string | number) => void }) {
  return (
    <article className={value ? "complete" : ""}>
      <div className="task-step-number">{String(index + 1).padStart(2, "0")}</div>
      <div>
        <h4>{step.title}</h4>
        <p>{step.instruction}</p>
        {step.example && <small>EXAMPLE: {step.example}</small>}
        {step.inputType === "check" ? <label className="task-check"><input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />MARK STEP COMPLETE</label>
          : <input aria-label={step.title} type={step.inputType === "url" ? "url" : step.inputType === "number" ? "number" : "text"} value={typeof value === "boolean" ? "" : value ?? ""} onChange={(event) => onChange(step.inputType === "number" ? Number(event.target.value) : event.target.value)} />}
      </div>
    </article>
  );
}
